// Default colors
const DEFAULT_COLOR: [number, number, number] = [82, 82, 91]; // Lighter grey (zinc-600)

// Cache for storing previously extracted colors to avoid redundant processing
const colorCache = new Map<string, [number, number, number]>();

// Flag to track if we're currently transitioning colors
// let isTransitioning = false;
// let transitionInterval: NodeJS.Timeout | null = null;

// Function to enhance a color by increasing saturation slightly
export function enhanceColor([r, g, b]: [number, number, number]): [number, number, number] {
  // Simple enhancement to make colors more vibrant
  const maxComponent = Math.max(r, g, b);
  const saturationIncrease = 1.2; // Saturation boost factor

  if (maxComponent === 0) return DEFAULT_COLOR;

  // Calculate enhancement while keeping values in valid range
  const enhancedR = Math.min(255, Math.round(r * saturationIncrease));
  const enhancedG = Math.min(255, Math.round(g * saturationIncrease));
  const enhancedB = Math.min(255, Math.round(b * saturationIncrease));

  return [enhancedR, enhancedG, enhancedB];
}

// Function to get the average color of an image with enhanced vibrancy
export async function getAverageColor(imageUrl: string): Promise<[number, number, number]> {
  // Check if we already have this color in cache
  const cacheKey = getUrlCacheKey(imageUrl);
  if (colorCache.has(cacheKey)) {
    return colorCache.get(cacheKey) as [number, number, number];
  }

  return new Promise((resolve) => {
    // Use a smaller version of the image for color extraction
    const optimizedUrl = getOptimizedUrlForColorExtraction(imageUrl);

    const img = new Image();
    img.crossOrigin = "Anonymous";

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      if (!context) {
        resolve(DEFAULT_COLOR);
        return;
      }

      // Set canvas size to a smaller dimension for better performance
      const size = 25; // Even smaller for faster processing
      canvas.width = size;
      canvas.height = size;

      // Draw and scale the image
      context.drawImage(img, 0, 0, size, size);
      const imageData = context.getImageData(0, 0, size, size).data;

      // Store all colors for analysis
      const colors: Array<[number, number, number, number]> = [];

      // Sample pixels and store colors with their brightness
      for (let i = 0; i < imageData.length; i += 4) {
        // Ensure we don't read past the end of the array
        if (i + 2 >= imageData.length) {
          break; // Stop if we can't read a full RGB triplet
        }
        const r = imageData[i];
        const g = imageData[i + 1];
        const b = imageData[i + 2];
        // Use non-null assertion since the check above guarantees existence
        const brightness = (r! * 299 + g! * 587 + b! * 114) / 1000;

        // Skip very dark or very light colors
        if (brightness > 20 && brightness < 250) {
          // Use non-null assertion here too
          colors.push([r!, g!, b!, brightness]);
        }
      }

      // If no valid colors found, return the default color
      if (colors.length === 0) {
        resolve(DEFAULT_COLOR);
        return;
      }

      // Sort colors by brightness
      colors.sort((a, b) => b[3] - a[3]);

      // Get colors from different brightness ranges
      const brightColors = colors.filter(c => c[3] > 128);
      const midColors = colors.filter(c => c[3] >= 64 && c[3] <= 128);
      const darkColors = colors.filter(c => c[3] < 64);

      // Function to calculate color saturation
      const getSaturation = (r: number, g: number, b: number): number => {
        const max = Math.max(r, g, b);
        const min = Math.min(r, g, b);
        return max === 0 ? 0 : (max - min) / max;
      };

      // Function to find the most saturated color in an array
      const getMostSaturatedColor = (colorArray: Array<[number, number, number, number]>): [number, number, number] => {
        let maxSaturation = -1;
        let bestColor: [number, number, number] = [0, 0, 0];

        colorArray.forEach(([r, g, b]) => {
          const saturation = getSaturation(r, g, b);
          if (saturation > maxSaturation) {
            maxSaturation = saturation;
            bestColor = [r, g, b];
          }
        });

        return bestColor;
      };

      // Try to get a vibrant color from the mid-brightness range first
      let resultColor: [number, number, number];
      if (midColors.length > 0) {
        resultColor = enhanceColor(getMostSaturatedColor(midColors));
      }
      // Then try bright colors
      else if (brightColors.length > 0) {
        resultColor = enhanceColor(getMostSaturatedColor(brightColors));
      }
      // Finally try dark colors
      else if (darkColors.length > 0) {
        resultColor = enhanceColor(getMostSaturatedColor(darkColors));
      }
      // Fallback to the most common color if no good options found
      else {
        resultColor = enhanceColor([
          Math.round(colors.reduce((sum, c) => sum + c[0], 0) / colors.length),
          Math.round(colors.reduce((sum, c) => sum + c[1], 0) / colors.length),
          Math.round(colors.reduce((sum, c) => sum + c[2], 0) / colors.length)
        ]);
      }

      // Cache the result for future use
      colorCache.set(cacheKey, resultColor);
      resolve(resultColor);
    };

    img.onerror = () => {
      resolve(DEFAULT_COLOR); // Default color on error
    };

    img.src = optimizedUrl;
  });
}

// Basic application of color to CSS variables (simple version)
export function applyColorToApp(color: [number, number, number]): void {
  document.documentElement.style.setProperty('--project-color', `${color[0]}, ${color[1]}, ${color[2]}`);
}

// Basic reset of color to default (simple version)
export function resetAppColors(): void {
  document.documentElement.style.setProperty('--project-color', `${DEFAULT_COLOR[0]}, ${DEFAULT_COLOR[1]}, ${DEFAULT_COLOR[2]}`);
}

// Gets a standardized URL for caching purposes
function getUrlCacheKey(url: string): string {
  // Provide an empty string fallback if split returns undefined
  return url.split('?')[0] ?? '';
}

// Returns an optimized version of the URL for color extraction
function getOptimizedUrlForColorExtraction(url: string): string {
  if (url.includes('unsplash.com')) {
    // Use a tiny version of the image for color extraction
    const baseUrl = url.split('?')[0];
    return `${baseUrl}?w=50&q=20&auto=format`;
  }
  return url;
}

// Function to get current color from CSS
// function getCurrentColor(): [number, number, number] {
//   const root = document.documentElement;
//   const color = getComputedStyle(root).getPropertyValue('--project-color').trim();
//   if (!color) return DEFAULT_COLOR;
//   const [r, g, b] = color.split(',').map(c => parseInt(c.trim()));
//   return [r || DEFAULT_COLOR[0], g || DEFAULT_COLOR[1], b || DEFAULT_COLOR[2]];
// }

// Function to interpolate between two colors
// function interpolateColor(color1: [number, number, number], color2: [number, number, number], factor: number): [number, number, number] {
//   return [
//     Math.round(color1[0] + (color2[0] - color1[0]) * factor),
//     Math.round(color1[1] + (color2[1] - color1[1]) * factor),
//     Math.round(color1[2] + (color2[2] - color1[2]) * factor)
//   ];
// }

// Debounce utility function
function debounce<T extends (...args: unknown[]) => unknown>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: number | null = null;

  return function (...args: Parameters<T>): void {
    const later = () => {
      timeout = null;
      func(...args);
    };

    if (timeout !== null) {
      window.clearTimeout(timeout);
    }

    timeout = window.setTimeout(later, wait);
  };
}

// Debounced version of the color application
export const debouncedApplyColor = debounce(
  (color: [number, number, number]) => applyColorToApp(color),
  50
);

// Initialize the app with default colors
export function initializeAppColors() {
  resetAppColors();
} 